import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { MTag } from '@/components/mobile/MobileKit';
import { marathonCard, splitMarathons, type MarathonCardData, type MarathonCardSource } from '@/lib/marathonCards';
import { cn } from '@/lib/utils';

/**
 * «Мои марафоны» — все курсы ученика карточками (D23, 11.09.2026, образец
 * `docs/refs/student-classes/`). Стоят сверху вкладки «Уроки» вместо полосы
 * прогресса основного курса и плиток «Дополнительные курсы»: у плиток не было ни
 * процента, ни числа пройденных уроков.
 *
 * Прогресс — `my-progress` на каждый курс с тем же ключом, что у страницы курса:
 * числа в карточке и на странице совпадают, а основной курс второй раз не
 * запрашивается. Разметка одна на обе ширины, раскладка — через `md:` (порог тот
 * же, что у `useIsMobile`).
 */
export function MyMarathons({ list, className }: { list: MarathonCardSource[]; className?: string }) {
  const progress = useQueries({
    queries: list.map((m) => ({
      queryKey: ['student', 'marathon-progress', m.id],
      queryFn: () => progressApi.getMyMarathonProgress(m.id),
    })),
  });
  const cards = list.map((m, i) => marathonCard(m, progress[i]?.data));
  if (cards.length === 0) return null;
  return <MarathonCardsList cards={cards} className={className} />;
}

export function MarathonCardsList({ cards, className }: { cards: MarathonCardData[]; className?: string }) {
  const { t } = useTranslation();
  const { active, completed } = splitMarathons(cards);
  return (
    <section className={cn('space-y-3', className)}>
      <h3 className="mx-0.5 font-display text-[15.5px] font-bold text-on-surface">{t('myMarathons.title')}</h3>
      {active.map((c) => (
        <MarathonCard key={c.id} card={c} />
      ))}
      {completed.length > 0 && (
        <>
          <h4 data-section="completed" className="mx-0.5 pt-2 text-[13px] font-semibold text-on-surface-variant">
            {t('myMarathons.completed')}
          </h4>
          {completed.map((c) => (
            <MarathonCard key={c.id} card={c} />
          ))}
        </>
      )}
    </section>
  );
}

function MarathonCard({ card }: { card: MarathonCardData }) {
  const { t } = useTranslation();
  // Битая обложка — та же заглушка с буквой, что и пустая: значок сломанной
  // картинки на карточке курса выглядит как поломка всего экрана.
  const [broken, setBroken] = useState(false);
  const cover = !broken ? card.coverUrl : null;
  const ready = card.percent !== null;

  return (
    <article
      data-marathon-card={card.id}
      className="rounded-[20px] bg-surface-container p-4 md:flex md:items-start md:gap-5 md:p-5"
    >
      <div className="flex min-w-0 flex-1 gap-3.5 md:gap-5">
        <span className="h-16 w-16 flex-none overflow-hidden rounded-[15px] bg-primary-container md:h-24 md:w-24">
          {cover ? (
            <img
              src={cover}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
              onError={() => setBroken(true)}
            />
          ) : (
            <span
              data-cover-fallback
              className="grid h-full w-full place-items-center font-display text-2xl font-bold text-on-primary-container md:text-[32px]"
            >
              {card.initial}
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          <h4 className="line-clamp-2 font-display text-[15px] font-bold leading-snug text-on-surface md:text-[17px]">
            {card.title}
          </h4>
          {/* Открытому курсу без записи метки типа нет (`marathonCards.ts`). */}
          {card.typeKey && (
            <div data-type-tag className="mt-1.5 flex">
              <MTag tone={card.typeKey === 'myMarathons.typeMarathon' ? 'warm' : 'info'}>{t(card.typeKey)}</MTag>
            </div>
          )}
          <div className="mt-3 flex items-center gap-3">
            <ProgressRing percent={card.percent} />
            <div className="min-w-0">
              <div className="text-[11.5px] font-semibold text-on-surface-variant">{t('myMarathons.passed')}</div>
              <div className="text-[13.5px] font-bold tabular-nums text-on-surface">
                {ready ? t('myMarathons.lessonsOf', { done: card.done, total: card.total }) : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 md:mt-0 md:flex-none">
        {/* Классы кнопки `Button` (primary, md = 44px). Сам `Button` не годится:
            кнопка внутри ссылки — недопустимая вложенность, читалка экрана
            объявила бы два элемента. На телефоне — во всю ширину. */}
        <Link
          to={card.enterTo}
          data-action="enter"
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-button bg-gradient-to-b from-primary to-primary-pressed px-5 font-display text-base font-semibold text-on-primary shadow-soft transition hover:shadow-ambient hover:brightness-105 active:scale-[0.98] md:w-auto"
        >
          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
            arrow_forward
          </span>
          {t('myMarathons.enter')}
        </Link>
        <Link
          to={card.statsTo}
          data-action="stats"
          aria-label={t('myMarathons.stats')}
          title={t('myMarathons.stats')}
          className="grid h-11 w-11 flex-none place-items-center rounded-button bg-surface-container-high text-on-surface-variant transition hover:text-on-surface"
        >
          {/* ⚠️ Не `bar_chart`: его нет в вырезанном шрифте, и вместо значка
              встало бы слово «BAR_CHART» (так уже было на главной 01.09.2026,
              сторож `iconSubset.test.ts`). `leaderboard` занят вкладкой «Рейтинг». */}
          <span className="material-symbols-outlined text-[22px]" aria-hidden="true">
            monitoring
          </span>
        </Link>
      </div>
    </article>
  );
}

/** Кружок с процентом. Пока прогресс не пришёл — прочерк: «0%» читался бы как «ничего не сделано». */
function ProgressRing({ percent }: { percent: number | null }) {
  const r = 16;
  const length = 2 * Math.PI * r;
  return (
    <span
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent ?? undefined}
      className="relative grid h-11 w-11 flex-none place-items-center"
    >
      <svg viewBox="0 0 40 40" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="20" cy="20" r={r} fill="none" stroke="currentColor" strokeWidth="4" className="text-outline" />
        {percent !== null && percent > 0 && (
          <circle
            cx="20"
            cy="20"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={length}
            strokeDashoffset={length * (1 - percent / 100)}
            className="text-primary"
          />
        )}
      </svg>
      <span className="relative text-[10.5px] font-bold tabular-nums text-on-surface">
        {percent !== null ? `${percent}%` : '—'}
      </span>
    </span>
  );
}
